import * as React from "react";

// NPM
import clsx from "clsx";
import { animated } from "react-spring";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
	useTheme,
} from "@material-ui/core/styles";
import { Button, MobileStepper, Snackbar, Typography } from "@material-ui/core";

// Utility

import { STEP_COUNT } from "../../domain/utility/config";

// Icons
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";

//Classes
import Utility from "../../domain/classes/Utility";
import Journey from "../../domain/classes/Journey";

// Contexts
import { useApplicationManager } from "../../contexts/ApplicationManager.Context";
import JourneyStage from "../../domain/enums/JourneyStage";

export const styles = (theme: Theme) =>
	createStyles({
		root: {
			width: "100%",
			zIndex: theme.zIndex.drawer - 1,
			position: "sticky",
			top: theme.spacing(6),
			backgroundColor: "white",
		},
		stepper: {
			borderBottom: "1px solid #EFEFEF",
			width: "100%",
			top: theme.spacing(6),
		},
		progress: {
			height: 20,
			borderRadius: 10,
		},
		progressText: {
			color: theme.palette.getContrastText(theme.palette.primary.main),
			position: "absolute",
			top: "50%",
			left: "50%",
			height: 20,
			transform: "translate(-50%, -50%)",
			zIndex: 100,
			display: "flex",
		},
	});

export interface ProgressStepperProps
	extends React.HTMLAttributes<HTMLDivElement> {}

const ProgressStepper = React.forwardRef<
	HTMLDivElement,
	ProgressStepperProps & WithStyles<typeof styles>
>(({ classes, className, ...divProps }, ref) => {
	// Context
	const theme = useTheme();

	const {
		journeyError,
		handleJourneyErrorChange: handleErrorChange,
		handleJourneyStageChange,
		journeyStage,
		conditionalProgress,
		clearApplicationData,
		clearCookies,
	} = useApplicationManager();

	// States
	const [snackbarOpen, setSnackbarOpen] = React.useState<boolean>(false);

	React.useEffect(() => {
		if (journeyError) {
			setSnackbarOpen(true);
		} else {
			setSnackbarOpen(false);
		}
	}, [journeyError]);

	// Handlers
	const handleNext = async () => {
		if (process.env.NODE_ENV !== "production") {
			console.log(conditionalProgress);
		}

		if (conditionalProgress.next) {
			const result = await conditionalProgress.next();
			if (result.success) {
				handleJourneyStageChange(
					Journey.goFrom(journeyStage, "forward")
				);
			} else {
				handleErrorChange(result.error);
			}
		} else {
			handleJourneyStageChange(Journey.goFrom(journeyStage, "forward"));
		}
	};

	const handleBack = () => {
		const destination = Journey.goFrom(journeyStage, "backward");

		if (destination === JourneyStage.AppointmentTypeSelection) {
			clearApplicationData();
			clearCookies();
		}

		handleJourneyStageChange(destination);
	};

	const handleClose = (
		event: React.SyntheticEvent | MouseEvent,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		handleErrorChange("");
		setSnackbarOpen(false);
	};

	const currentStep = Journey.isAtStep(journeyStage);

	const percentage =
		currentStep + 1 === STEP_COUNT
			? "COMPLETE"
			: ((currentStep / STEP_COUNT) * 100).toFixed(0) + "%";

	return (
		<div className={clsx(classes.root, className)} {...divProps} ref={ref}>
			<Typography
				variant="caption"
				align="center"
				color="primary"
				className={classes.progressText}
			>
				{percentage}
			</Typography>
			<MobileStepper
				variant="progress"
				steps={STEP_COUNT}
				position="static"
				activeStep={currentStep}
				className={classes.stepper}
				classes={{ progress: classes.progress }}
				nextButton={
					<Button
						size="small"
						onClick={handleNext}
						disabled={Journey.disableNextButton(journeyStage)}
					>
						Next
						{theme.direction === "rtl" ? (
							<KeyboardArrowLeft />
						) : (
							<KeyboardArrowRight />
						)}
					</Button>
				}
				backButton={
					<Button
						size="small"
						onClick={handleBack}
						disabled={Journey.disableBackButton(journeyStage)}
					>
						{theme.direction === "rtl" ? (
							<KeyboardArrowRight />
						) : (
							<KeyboardArrowLeft />
						)}
						Back
					</Button>
				}
			/>

			<Snackbar
				message={journeyError}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				open={snackbarOpen}
				autoHideDuration={2000}
				onClose={handleClose}
			/>
		</div>
	);
});

const StyledProgressStepper = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("ProgressStepper"),
})(ProgressStepper);

export default StyledProgressStepper;

export const AnimatedProgressStepper = animated(StyledProgressStepper);
